import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { snakeCase } from 'lodash';
import type { AgentMentionedToolItem } from '@cotera/api';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { Assert } from '@cotera/utilities';

export type AgentMentionToolsMode =
  | { mode: 'viewer' }
  | {
      mode: 'owner_org';
      toolsRunInOwnerOrg: boolean;
      toolLookup: Map<string, AgentMentionedToolItem>;
      isLoading: boolean;
    };

const AgentMentionToolsContext = createContext<AgentMentionToolsMode | null>(
  null
);

function buildToolLookup(
  tools: AgentMentionedToolItem[]
): Map<string, AgentMentionedToolItem> {
  const m = new Map<string, AgentMentionedToolItem>();
  for (const t of tools) {
    m.set(snakeCase(t.key), t);
    m.set(t.key, t);
  }
  return m;
}

/**
 * When the agent lives in another org, tool @-mentions should reflect the owner's org
 * (same as runtime). Otherwise exposes `viewer` mode — mentions use the normal tools list.
 */
export const AgentMentionToolsProvider: React.FC<{
  agentId: string;
  agentOrgId: string;
  agentUpdatedAt: string;
  children: React.ReactNode;
}> = ({ agentId, agentOrgId, agentUpdatedAt, children }) => {
  const org = useWhoami((s) => s.org);
  const client = useTenantedClient();
  const isForeignAgent = agentOrgId !== org.id;

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.resource.agentMentionedToolsStatus({
      orgId: org.id,
      agentId,
      agentUpdatedAt,
    }),
    queryFn: async () => {
      return Assert.assertOk(
        await client.resource.agent.getMentionedToolsStatus({ id: agentId })
      );
    },
    enabled: isForeignAgent,
  });

  const value = useMemo((): AgentMentionToolsMode => {
    if (!isForeignAgent) {
      return { mode: 'viewer' };
    }
    return {
      mode: 'owner_org',
      toolsRunInOwnerOrg: data?.toolsRunInOwnerOrg ?? false,
      toolLookup: buildToolLookup(data?.tools ?? []),
      isLoading,
    };
  }, [isForeignAgent, data, isLoading]);

  return (
    <AgentMentionToolsContext.Provider value={value}>
      {children}
    </AgentMentionToolsContext.Provider>
  );
};

export function useAgentMentionToolsMode(): AgentMentionToolsMode {
  return (
    useContext(AgentMentionToolsContext) ?? {
      mode: 'viewer' as const,
    }
  );
}

export function lookupMentionedTool(
  toolLookup: Map<string, AgentMentionedToolItem>,
  toolKey: string
): AgentMentionedToolItem | null {
  return toolLookup.get(toolKey) ?? toolLookup.get(snakeCase(toolKey)) ?? null;
}
